const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

const BUILD_RELOAD_KEY = "pwa-home-build-reload";
const HOME_REFRESH_AT_KEY = "pwa-home-refresh-at";
/** Skip repeat home refresh work within this window. */
const HOME_REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

function getServiceWorkerUrl() {
  return `${process.env.PUBLIC_URL || ""}/pwa-sw.js`;
}

const HOME_BG_CACHE = "gallery-kiosk-home-bg-v1";
const CDN_IMAGE_CACHE = "gallery-kiosk-cdn-images-v1";
const KEEP_CACHES = new Set([HOME_BG_CACHE, CDN_IMAGE_CACHE]);

/** Drop Cache Storage except home bg + CDN artwork images. */
export async function clearAppCaches() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((key) => !KEEP_CACHES.has(key))
          .map((key) => caches.delete(key))
      );
    }
  } catch {
    /* ignore */
  }

  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    reg?.active?.postMessage({ type: "CLEAR_CACHES" });
  } catch {
    /* ignore */
  }
}

function listenForWaitingWorker(registration) {
  if (registration.waiting) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    if (!worker) return;
    worker.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller) {
        worker.postMessage({ type: "SKIP_WAITING" });
      }
    });
  });
}

function getLoadedMainScript() {
  for (const el of document.querySelectorAll("script[src]")) {
    try {
      const path = new URL(el.src, window.location.origin).pathname;
      if (/\/static\/js\/main\./.test(path)) return path;
    } catch {
      /* ignore */
    }
  }
  return null;
}

/** Compare live index.html main bundle to the one currently running. */
async function getRemoteMainScript() {
  const base = process.env.PUBLIC_URL || "";
  const url = `${base}/index.html?_=${Date.now()}`;
  const res = await fetch(url, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
  });
  if (!res.ok) return null;
  const html = await res.text();
  const match = html.match(/\/static\/js\/main\.[^"'>\s]+\.js/);
  return match ? match[0] : null;
}

async function shouldReloadForNewBuild() {
  try {
    const remoteMain = await getRemoteMainScript();
    const localMain = getLoadedMainScript();
    if (!remoteMain || !localMain) return false;

    const remoteFile = remoteMain.split("/").pop();
    const localFile = localMain.split("/").pop();
    if (!remoteFile || remoteFile === localFile) return false;

    try {
      if (sessionStorage.getItem(BUILD_RELOAD_KEY) === remoteFile) {
        return false;
      }
      sessionStorage.setItem(BUILD_RELOAD_KEY, remoteFile);
    } catch {
      /* ignore */
    }
    return true;
  } catch {
    return false;
  }
}

function reloadOnceForController() {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      window.clearTimeout(timer);
      window.location.reload();
      resolve(true);
    };
    const onChange = () => finish();
    const timer = window.setTimeout(() => {
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve(false);
    }, 2500);
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
  });
}

function isHomeRefreshCoolingDown() {
  try {
    const last = Number(sessionStorage.getItem(HOME_REFRESH_AT_KEY) || 0);
    return last > 0 && Date.now() - last < HOME_REFRESH_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markHomeRefresh() {
  try {
    sessionStorage.setItem(HOME_REFRESH_AT_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function register() {
  if (!("serviceWorker" in navigator)) return;

  const start = async () => {
    const swUrl = getServiceWorkerUrl();
    if (isLocalhost || process.env.NODE_ENV === "development") {
      checkValidServiceWorker(swUrl);
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register(swUrl, {
        updateViaCache: "none",
      });
      registration.update().catch(() => undefined);
      listenForWaitingWorker(registration);
    } catch (error) {
      console.error("Error during service worker registration:", error);
    }
  };

  if (document.readyState === "complete") {
    void start();
    return;
  }
  window.addEventListener("load", () => {
    void start();
  });
}

/**
 * On home (/): light update check (throttled). Does not clear caches every visit.
 */
export async function refreshAppOnHome() {
  if (isHomeRefreshCoolingDown()) return;
  markHomeRefresh();

  if (!("serviceWorker" in navigator)) {
    if (await shouldReloadForNewBuild()) {
      window.location.reload();
    }
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update().catch(() => undefined);

      if (registration.waiting) {
        const reloading = reloadOnceForController();
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        if (await reloading) return;
      }
    }
  } catch {
    /* ignore */
  }

  if (await shouldReloadForNewBuild()) {
    window.location.reload();
  }
}

function checkValidServiceWorker(swUrl) {
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
    cache: "no-store",
  })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister();
        });
      } else {
        navigator.serviceWorker
          .register(swUrl, { updateViaCache: "none" })
          .catch(() => undefined);
      }
    })
    .catch(() => {
      navigator.serviceWorker
        .register(swUrl, { updateViaCache: "none" })
        .catch(() => undefined);
    });
}

export function unregister() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch((error) => {
      console.error(error.message);
    });
}
